<script setup lang="ts">
    import Icon from '@/components/Icon.vue';
    import {
        DatePickerArrow,
        DatePickerCalendar,
        DatePickerCell,
        DatePickerCellTrigger,
        DatePickerContent,
        DatePickerField,
        DatePickerGrid,
        DatePickerGridBody,
        DatePickerGridHead,
        DatePickerGridRow,
        DatePickerHeadCell,
        DatePickerHeader,
        DatePickerHeading,
        DatePickerInput,
        DatePickerNext,
        DatePickerPrev,
        DatePickerRoot,
        DatePickerTrigger,
    } from 'reka-ui';
    import { useI18n } from 'vue-i18n';
    import { ref, watch, type Ref } from 'vue';
    import { CalendarDate } from '@internationalized/date';
    import type { DateValue } from 'reka-ui';
    import { useDateFormat } from '@/composables/useDateFormat';
    import type { HTMLAttributes } from 'vue';
    import { cn } from '@/lib/utils';

    const props = withDefaults(
        defineProps<{
            modelValue?: any;
            disabled?: boolean;
            minDate?: DateValue;
            maxDate?: DateValue;
            class?: HTMLAttributes['class'];
            showInput?: boolean;
        }>(),
        {
            showInput: true,
        },
    );

    const emit = defineEmits<{
        (e: 'update:modelValue', value: any): void;
    }>();

    const { parseDateString } = useDateFormat();

    // Convert string to CalendarDate if needed
    const convertToCalendarDate = (value: any): DateValue | null => {
        if (!value) return null;

        // If it's already a CalendarDate, return it
        if (value instanceof CalendarDate) {
            return value;
        }

        // If it's a string, try to parse it
        if (typeof value === 'string') {
            return parseDateString(value);
        }

        return null;
    };

    // Internal value that's always a DateValue
    const internalValue = ref<DateValue | null>(
        convertToCalendarDate(props.modelValue),
    ) as Ref<DateValue | null>;

    // Watch for external changes
    watch(
        () => props.modelValue,
        (newValue) => {
            internalValue.value = convertToCalendarDate(newValue);
        },
    );

    // Emit changes back to parent - always emit in "yyyy-mm-dd" format
    const emitValue = (value: DateValue | null) => {
        if (value) {
            // Always emit as string in "yyyy-mm-dd" format
            emit('update:modelValue', value.toString());
        } else {
            emit('update:modelValue', null);
        }
    };

    watch(internalValue, (newValue) => {
        emitValue(newValue);
    });

    if (internalValue.value) {
        emitValue(internalValue.value);
    }
</script>

<template>
    <div class="flex flex-col gap-2">
        <DatePickerRoot
            v-model="internalValue"
            locale="da"
            :disabled="disabled"
            :min-value="minDate"
            :max-value="maxDate"
            close-on-select
        >
            <DatePickerField
                v-slot="{ segments }"
                :class="
                    cn(
                        'data-[invalid]:border-destructive border-input data-[disabled]:bg-faded flex h-7.5 w-full min-w-0 items-center rounded-md border bg-transparent px-2.5 py-1 text-xs shadow-xs transition-[color,box-shadow] outline-none',
                        props.class,
                    )
                "
            >
                <div
                    v-if="showInput"
                    class="mr-2 flex items-center"
                >
                    <template
                        v-for="item in segments"
                        :key="item.part"
                    >
                        <DatePickerInput
                            v-if="item.part === 'literal'"
                            :part="item.part"
                        >
                            {{ item.value }}
                        </DatePickerInput>
                        <DatePickerInput
                            v-else
                            :part="item.part"
                            class="data-[placeholder]:text-muted-foreground focus:bg-muted rounded-xs p-0.5 focus:outline-none"
                        >
                            {{ item.value }}
                        </DatePickerInput>
                    </template>
                </div>

                <DatePickerTrigger
                    class="hover:text-primary ml-auto cursor-pointer rounded focus:outline-none"
                >
                    <Icon
                        name="calendar"
                        class="size-3.5"
                    />
                </DatePickerTrigger>
            </DatePickerField>

            <DatePickerContent
                :side-offset="4"
                class="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 z-50 rounded-xl border bg-white shadow-sm will-change-[transform,opacity]"
            >
                <DatePickerArrow class="fill-white stroke-gray-300" />
                <DatePickerCalendar
                    v-slot="{ weekDays, grid }"
                    class="p-4"
                >
                    <DatePickerHeader class="flex items-center justify-between">
                        <DatePickerPrev
                            class="hover:bg-background text-foreground inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md bg-transparent active:scale-98 active:transition-all"
                        >
                            <Icon
                                name="chevronLeft"
                                class="h-4 w-4"
                            />
                        </DatePickerPrev>

                        <DatePickerHeading class="text-foreground font-medium" />
                        <DatePickerNext
                            class="hover:bg-background text-foreground inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md bg-transparent active:scale-98 active:transition-all"
                        >
                            <Icon
                                name="chevronRight"
                                class="h-4 w-4"
                            />
                        </DatePickerNext>
                    </DatePickerHeader>
                    <div class="flex flex-col space-y-4 pt-4 sm:flex-row sm:space-y-0 sm:space-x-4">
                        <DatePickerGrid
                            v-for="month in grid"
                            :key="month.value.toString()"
                            class="w-full border-collapse space-y-1 select-none"
                        >
                            <DatePickerGridHead>
                                <DatePickerGridRow class="mb-1 flex w-full justify-between">
                                    <DatePickerHeadCell
                                        v-for="day in weekDays"
                                        :key="day"
                                        class="text-green8 w-8 rounded-md text-xs"
                                    >
                                        {{ day }}
                                    </DatePickerHeadCell>
                                </DatePickerGridRow>
                            </DatePickerGridHead>
                            <DatePickerGridBody>
                                <DatePickerGridRow
                                    v-for="(weekDates, index) in month.rows"
                                    :key="`weekDate-${index}`"
                                    class="flex w-full"
                                >
                                    <DatePickerCell
                                        v-for="weekDate in weekDates"
                                        :key="weekDate.toString()"
                                        :date="weekDate"
                                    >
                                        <DatePickerCellTrigger
                                            :day="weekDate"
                                            :month="month.value"
                                            class="data-[disabled]:text-faded-foreground data-[selected]:text-primary data-[selected]:bg-primary/10 hover:bg-primary/10 text-foreground before:bg-primary data-[today]:before:bg-primary data-[outside-view]:text-foreground/30 data-[unavailable]:text-foreground/30 relative flex h-8 w-8 items-center justify-center rounded-md border border-transparent bg-transparent text-xs font-normal whitespace-nowrap outline-none before:absolute before:top-[3px] before:hidden before:h-1 before:w-1 before:rounded-full data-[disabled]:hover:bg-transparent data-[selected]:font-medium data-[selected]:before:bg-white data-[today]:before:top-6 data-[today]:before:block data-[unavailable]:pointer-events-none data-[unavailable]:line-through"
                                        />
                                    </DatePickerCell>
                                </DatePickerGridRow>
                            </DatePickerGridBody>
                        </DatePickerGrid>
                    </div>
                </DatePickerCalendar>
            </DatePickerContent>
        </DatePickerRoot>
    </div>
</template>
